import React, { useState, useCallback } from 'react';
import { useTenantedClient } from '@cotera/client/app/stores/org';
import { CheckIcon, ChevronRightIcon } from 'lucide-react';
import { Button } from '@cotera/client/v0/components/ui/button';
import { SelectOptionCard } from '@cotera/client/v0/components/select-option-card';
import type { ToolRendererProps } from './index';

type UserOptionChoice = {
  value: string;
  name?: string;
  short?: string;
  description?: string;
  choices?: UserOptionChoice[];
};

type UserOptionsInput = {
  message: string;
  type: 'list' | 'checkbox' | 'rawlist' | 'expand';
  multiSelect?: boolean | null;
  choices: UserOptionChoice[];
};

type UserOptionsOutput = {
  selections?: string[];
};

/**
 * Renderer for coco/user-options.
 * When incomplete, shows an inquirer-style prompt: list, checkbox, rawlist, or expand.
 * Supports nested choices: selecting an option with sub-choices shows a follow-up prompt.
 */
export const UserOptionsRenderer: React.FC<
  ToolRendererProps & { embedded?: boolean; conversationId?: string }
> = ({ part, chatModel, readonly, embedded = false, conversationId }) => {
  const input = part.input as UserOptionsInput | undefined;
  const output = part.getOutput<UserOptionsOutput>();
  const showForm = part.isIncomplete() && !(readonly === true);

  // Use conversationId from hijacker props if available, otherwise fall back to chatModel.id
  const effectiveConversationId = conversationId ?? chatModel.id;

  if (showForm && input !== undefined) {
    return (
      <UserOptionsForm
        input={input}
        toolCallId={part.toolCallId}
        chatModel={chatModel}
        embedded={embedded}
        conversationId={effectiveConversationId}
      />
    );
  }

  if (part.isCompleted() && output?.selections !== undefined) {
    if (input !== undefined) {
      return (
        <CompletedOptionsSummary
          input={input}
          selections={output.selections}
          embedded={embedded}
        />
      );
    }
    // Fallback when the original options are unavailable.
    const labels = output.selections.join(', ');
    return (
      <div className="flex items-center gap-1.5 text-sm italic text-muted-foreground py-1">
        <CheckIcon className="h-3 w-3 text-emerald-600 dark:text-emerald-400" />
        <span>
          Selected: <span className="text-foreground">{labels}</span>
        </span>
      </div>
    );
  }

  return null;
};

/**
 * Read-only view shown once a selection has been made. Re-renders the original
 * options (dimming the ones that weren't chosen, check-marking the ones that
 * were) so the user can still see what the choices were.
 */
const CompletedOptionsSummary: React.FC<{
  input: UserOptionsInput;
  selections: string[];
  embedded?: boolean;
}> = ({ input, selections, embedded = false }) => {
  const [expanded, setExpanded] = useState(false);
  const selectedValues = new Set(selections);
  const choices = input.choices ?? [];
  const isMultiSelect = input.multiSelect === true || input.type === 'checkbox';
  const wrapperClassName = embedded
    ? 'flex min-h-0 min-w-0 flex-col px-4 pt-3 pb-2'
    : 'flex min-h-0 min-w-0 flex-col py-1';

  return (
    <div className={wrapperClassName}>
      <button
        type="button"
        onClick={() => setExpanded((prev) => !prev)}
        aria-expanded={expanded}
        className="flex w-full items-center gap-1.5 text-sm italic text-muted-foreground hover:text-foreground transition-colors"
      >
        <CheckIcon className="h-3 w-3 shrink-0 text-emerald-600 dark:text-emerald-400" />
        <span className="text-left">
          Selected:{' '}
          <span className="text-foreground">
            {selections
              .map((value) => getLabelForValue(value, choices))
              .join(', ')}
          </span>
        </span>
        <ChevronRightIcon
          className={`ml-auto h-4 w-4 shrink-0 transition-transform ${
            expanded ? 'rotate-90' : ''
          }`}
        />
      </button>
      {expanded && (
        <div className="mt-2 flex min-h-0 min-w-0 flex-col border border-border rounded-xl p-4 bg-card">
          <p className="font-medium text-sm mb-4 shrink-0">{input.message}</p>
          <div className="min-h-0 space-y-4 overflow-y-auto">
            {choices.map((choice, index) => {
              const value = getChoiceValue(choice);
              return (
                <SelectOptionCard
                  key={value || index}
                  selected={selectedValues.has(value)}
                  onSelect={() => {}}
                  disabled
                  role={isMultiSelect ? 'checkbox' : 'radio'}
                  title={getChoiceLabel(choice)}
                  subtitle={choice.description}
                  aria-label={getChoiceLabel(choice)}
                />
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

const getChoiceLabel = (choice: UserOptionChoice): string =>
  choice.name ?? choice.short ?? choice.value ?? '';

/** Value used for selection - LLM may omit value and only provide name/short */
const getChoiceValue = (choice: UserOptionChoice): string =>
  choice.value ?? choice.name ?? choice.short ?? '';

/**
 * Resolves a selected value back to its human-readable label by searching the
 * (possibly nested) choices. Falls back to the raw value when no match exists.
 */
const getLabelForValue = (
  value: string,
  choices: UserOptionChoice[] | undefined
): string => {
  for (const choice of choices ?? []) {
    if (getChoiceValue(choice) === value) {
      return getChoiceLabel(choice);
    }
    const nested = getLabelForValue(value, choice.choices);
    if (nested !== value) {
      return nested;
    }
  }
  return value;
};

const EXPAND_KEYS = [
  'h',
  'j',
  'k',
  'l',
  'n',
  'p',
  'q',
  'r',
  's',
  't',
  'u',
  'v',
  'w',
  'x',
  'y',
  'z',
  'a',
  'b',
  'c',
  'd',
  'e',
  'f',
  'g',
  'i',
  'm',
  'o',
];

const UserOptionsForm: React.FC<{
  input: UserOptionsInput;
  toolCallId: string;
  chatModel: ToolRendererProps['chatModel'];
  embedded?: boolean;
  conversationId: string;
}> = ({ input, toolCallId, chatModel, embedded = false, conversationId }) => {
  const client = useTenantedClient();
  const [submittedSelections, setSubmittedSelections] = useState<
    string[] | null
  >(null);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [selectionPath, setSelectionPath] = useState<string[]>([]);

  const choices = getCurrentChoices(input.choices, selectionPath);

  const handleSubmit = useCallback(
    async (selections: string[]) => {
      setSubmitError(null);
      try {
        const result = await client.resource.chat.runInterruptedTool({
          conversationId,
          toolCallId,
          model: chatModel.model,
          input: { selections },
        });
        if (result.isErr()) {
          setSubmitError('Failed to submit. Please try again.');
        } else {
          setSubmittedSelections(selections);
        }
      } catch {
        setSubmitError('Failed to submit. Please try again.');
      }
    },
    [client, conversationId, chatModel.model, toolCallId]
  );

  const handleSelect = useCallback(
    (value: string) => {
      const choice = choices.find((c) => getChoiceValue(c) === value);
      const hasNestedChoices = (choice?.choices?.length ?? 0) > 0;
      if (hasNestedChoices) {
        setSelectionPath((prev) => [...prev, value]);
      } else {
        void handleSubmit([...selectionPath, value]);
      }
    },
    [choices, selectionPath, handleSubmit]
  );

  if (submittedSelections !== null) {
    return (
      <CompletedOptionsSummary
        input={input}
        selections={submittedSelections}
        embedded={embedded}
      />
    );
  }

  const isMultiSelect = input.multiSelect === true || input.type === 'checkbox';

  if (isMultiSelect) {
    return (
      <CheckboxOptions
        message={input.message}
        choices={choices}
        onSubmit={handleSubmit}
        submitError={submitError}
        embedded={embedded}
      />
    );
  }

  const listType: 'list' | 'rawlist' | 'expand' =
    input.type === 'checkbox' ? 'list' : input.type;

  return (
    <ListOptions
      message={input.message}
      type={listType}
      choices={choices}
      selectionPath={selectionPath}
      onSelect={handleSelect}
      onBack={
        selectionPath.length > 0
          ? () => setSelectionPath(selectionPath.slice(0, -1))
          : undefined
      }
      onSubmit={handleSubmit}
      submitError={submitError}
      embedded={embedded}
    />
  );
};

function getCurrentChoices(
  rootChoices: UserOptionChoice[] | undefined,
  path: string[]
): UserOptionChoice[] {
  const choices = rootChoices ?? [];
  if (path.length === 0) {
    return choices;
  }
  const [head, ...rest] = path;
  const choice = choices.find((c) => getChoiceValue(c) === head);
  if (choice === undefined || choice.choices === undefined) {
    return [];
  }
  return getCurrentChoices(choice.choices, rest);
}

const CheckboxOptions: React.FC<{
  message: string;
  choices: UserOptionChoice[];
  onSubmit: (selections: string[]) => void;
  submitError: string | null;
  embedded?: boolean;
}> = ({ message, choices, onSubmit, submitError, embedded = false }) => {
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const safeChoices = choices ?? [];

  const toggle = (value: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(value)) {
        next.delete(value);
      } else {
        next.add(value);
      }
      return next;
    });
  };

  const wrapperClassName = embedded
    ? 'flex min-h-0 min-w-0 flex-col px-4 pt-3 pb-2'
    : 'flex min-h-0 min-w-0 flex-col border border-border rounded-xl p-4 bg-card';

  return (
    <div className={wrapperClassName}>
      <p className="font-medium text-sm mb-4 shrink-0">{message}</p>
      {/* The hijacked input caps this renderer's height, so the choices own the
          scrolling — otherwise options past the cutoff are unreachable. */}
      <div className="min-h-0 space-y-4 overflow-y-auto mb-4">
        {safeChoices.map((choice, index) => (
          <SelectOptionCard
            key={getChoiceValue(choice) || index}
            selected={selected.has(getChoiceValue(choice))}
            onSelect={() => toggle(getChoiceValue(choice))}
            role="checkbox"
            title={getChoiceLabel(choice)}
            subtitle={choice.description}
            aria-label={getChoiceLabel(choice)}
          />
        ))}
      </div>
      {submitError !== null && (
        <p className="text-sm text-destructive mb-2 shrink-0">{submitError}</p>
      )}
      <Button
        size="sm"
        className="shrink-0"
        onClick={() => void onSubmit(Array.from(selected))}
        disabled={selected.size === 0}
      >
        Confirm
      </Button>
    </div>
  );
};

const ListOptions: React.FC<{
  message: string;
  type: 'list' | 'rawlist' | 'expand';
  choices: UserOptionChoice[];
  selectionPath: string[];
  onSelect: (value: string) => void;
  onBack?: () => void;
  onSubmit: (selections: string[]) => void;
  submitError: string | null;
  embedded?: boolean;
}> = ({
  message,
  type,
  choices,
  selectionPath,
  onSelect,
  onBack,
  submitError,
  embedded = false,
}) => {
  const safeChoices = choices ?? [];
  const wrapperClassName = embedded
    ? 'flex min-h-0 min-w-0 flex-col px-4 pt-3 pb-2'
    : 'flex min-h-0 min-w-0 flex-col border border-border rounded-xl p-4 bg-card';

  if (safeChoices.length === 0) {
    return (
      <div className={wrapperClassName}>
        <p className="text-sm text-muted-foreground">No options available</p>
      </div>
    );
  }

  const handleSelect = (value: string) => {
    onSelect(value);
  };

  return (
    <div className={wrapperClassName}>
      {onBack !== undefined && (
        <Button
          variant="ghost"
          size="sm"
          className="mb-2 -ml-1 shrink-0 self-start"
          onClick={onBack}
        >
          <ChevronRightIcon className="h-4 w-4 rotate-180" />
          Back
        </Button>
      )}
      <p className="font-medium text-sm mb-4 shrink-0">
        {selectionPath.length > 0 ? 'Select an option:' : message}
      </p>
      {/* The hijacked input caps this renderer's height, so the choices own the
          scrolling — otherwise options past the cutoff are unreachable. */}
      <div className="min-h-0 space-y-4 overflow-y-auto mb-4">
        {safeChoices.map((choice, index) => {
          const value = getChoiceValue(choice);
          const label = getChoiceLabel(choice);
          const keyLabel =
            type === 'expand' && index < EXPAND_KEYS.length
              ? `(${EXPAND_KEYS[index]}) `
              : type === 'rawlist'
              ? `(${index + 1}) `
              : '';
          const displayLabel = `${keyLabel}${label}`;

          return (
            <SelectOptionCard
              key={value || index}
              selected={false}
              onSelect={() => handleSelect(value)}
              role="button"
              title={displayLabel}
              subtitle={choice.description}
              aria-label={label}
              rightSlot={
                (choice.choices?.length ?? 0) > 0 ? (
                  <ChevronRightIcon className="mt-1 h-4 w-4 shrink-0 text-muted-foreground" />
                ) : (
                  <div className="mt-1 h-4 w-4 shrink-0" aria-hidden />
                )
              }
            />
          );
        })}
      </div>
      {submitError !== null && (
        <p className="text-sm text-destructive mb-2 shrink-0">{submitError}</p>
      )}
    </div>
  );
};
